/**
 * PendingApprovalsQueue Component
 *
 * Displays a user's pending approval queue with:
 * - Filterable/sortable list of pending approvals
 * - Quick approve/reject actions
 * - Bulk actions
 * - Priority indicators
 * - Overdue warnings
 *
 * @module components/admin/approval-processes/PendingApprovalsQueue
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  Filter,
  ChevronDown,
  ChevronUp,
  User,
  Calendar,
  FileText,
  ArrowRight,
  MoreHorizontal,
  MessageSquare,
  RefreshCw,
  Eye,
  UserPlus,
  SortAsc,
  SortDesc,
  CheckSquare
} from 'lucide-react';
import { StandardButton } from '../../standard/StandardButton';
import { StandardModal } from '../../standard/StandardModal';
import { StandardCard } from '../../standard/StandardCard';
import { StandardSelect } from '../../standard/StandardSelect';
import { StandardTextarea } from '../../standard/StandardTextarea';
import { StandardSearchBar } from '../../standard/StandardSearchBar';
import { StandardLoadingState } from '../../standard/StandardLoadingState';
import { StandardEmptyState } from '../../standard/StandardEmptyState';
import { StandardCheckbox } from '../../standard/StandardCheckbox';
import { useToast } from '../../standard/StandardToast';
import {
  PendingApproval,
  ApprovalRequest,
  ApprovalAction,
  ApprovalActionPayload
} from '../../../types/approvalProcesses';
import { approvalProcessStorage } from '../../../services/approvalProcessStorage';
import { USE_GOBODO_API } from '../../../utils/gobodoUtils';

interface PendingApprovalsQueueProps {
  userId?: string; // If not provided, uses 'current-user' (mock)
  onViewRequest?: (requestId: string) => void;
  onViewRecord?: (recordId: string, objectType: string) => void;
}

type SortField = 'submittedDate' | 'dueDate' | 'processName' | 'recordName';
type SortDirection = 'asc' | 'desc';

interface ActionModalState {
  isOpen: boolean;
  action: ApprovalAction | null;
  requestId: string;
  recordName: string;
}

// Mock users for reassignment
const mockUsers = [
  { id: 'user-001', name: 'John Smith' },
  { id: 'user-002', name: 'Sarah Johnson' },
  { id: 'user-003', name: 'Mike Wilson' },
  { id: 'user-004', name: 'Emily Davis' },
  { id: 'user-005', name: 'Robert Brown' }
];

export const PendingApprovalsQueue: React.FC<PendingApprovalsQueueProps> = ({
  userId = 'current-user',
  onViewRequest,
  onViewRecord
}) => {
  const toast = useToast();
  const [pendingApprovals, setPendingApprovals] = useState<PendingApproval[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [objectTypeFilter, setObjectTypeFilter] = useState<string>('all');
  const [overdueFilter, setOverdueFilter] = useState<string>('all');
  const [sortField, setSortField] = useState<SortField>('submittedDate');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [expandedItem, setExpandedItem] = useState<string | null>(null);
  const [actionModal, setActionModal] = useState<ActionModalState>({
    isOpen: false,
    action: null,
    requestId: '',
    recordName: ''
  });
  const [actionComments, setActionComments] = useState('');
  const [reassignUserId, setReassignUserId] = useState('');
  const [processingAction, setProcessingAction] = useState(false);

  // Load pending approvals
  useEffect(() => {
    loadPendingApprovals();
  }, [userId]);

  const loadPendingApprovals = () => {
    setLoading(true);
    try {
      const approvals = USE_GOBODO_API ? [] : approvalProcessStorage.getPendingApprovalsForUser(userId);
      setPendingApprovals(approvals);
    } catch (error) {
      toast.error('Error', 'Failed to load pending approvals');
    } finally {
      setLoading(false);
    }
  };

  // Get unique object types for filter
  const objectTypes = useMemo(() => {
    const types = new Set(pendingApprovals.map(a => a.objectType));
    return Array.from(types).sort();
  }, [pendingApprovals]);

  // Filter and sort approvals
  const filteredApprovals = useMemo(() => {
    let result = [...pendingApprovals];

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(a =>
        a.recordName.toLowerCase().includes(query) ||
        a.processName.toLowerCase().includes(query) ||
        a.submittedByName.toLowerCase().includes(query)
      );
    }

    // Object type filter
    if (objectTypeFilter !== 'all') {
      result = result.filter(a => a.objectType === objectTypeFilter);
    }

    // Overdue filter
    if (overdueFilter === 'overdue') {
      result = result.filter(a => a.isOverdue);
    } else if (overdueFilter === 'on-track') {
      result = result.filter(a => !a.isOverdue);
    }

    // Sort
    result.sort((a, b) => {
      let comparison = 0;
      switch (sortField) {
        case 'submittedDate':
          comparison = new Date(a.submittedDate).getTime() - new Date(b.submittedDate).getTime();
          break;
        case 'dueDate':
          const aDue = a.dueDate ? new Date(a.dueDate).getTime() : Infinity;
          const bDue = b.dueDate ? new Date(b.dueDate).getTime() : Infinity;
          comparison = aDue - bDue;
          break;
        case 'processName':
          comparison = a.processName.localeCompare(b.processName);
          break;
        case 'recordName':
          comparison = a.recordName.localeCompare(b.recordName);
          break;
      }
      return sortDirection === 'asc' ? comparison : -comparison;
    });

    return result;
  }, [pendingApprovals, searchQuery, objectTypeFilter, overdueFilter, sortField, sortDirection]);

  // Handle sort
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Toggle item selection
  const toggleItemSelection = (id: string) => {
    const newSelected = new Set(selectedItems);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedItems(newSelected);
  };

  // Select all
  const selectAll = () => {
    if (selectedItems.size === filteredApprovals.length) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(filteredApprovals.map(a => a.requestId)));
    }
  };

  // Open action modal
  const openActionModal = (action: ApprovalAction, requestId: string, recordName: string) => {
    setActionModal({
      isOpen: true,
      action,
      requestId,
      recordName
    });
    setActionComments('');
    setReassignUserId('');
  };

  // Close action modal
  const closeActionModal = () => {
    setActionModal({
      isOpen: false,
      action: null,
      requestId: '',
      recordName: ''
    });
    setActionComments('');
    setReassignUserId('');
  };

  // Process action
  const processAction = async () => {
    if (!actionModal.action || !actionModal.requestId) return;

    setProcessingAction(true);
    try {
      const payload: ApprovalActionPayload = {
        requestId: actionModal.requestId,
        action: actionModal.action,
        comments: actionComments || undefined,
        reassignToUserId: actionModal.action === 'reassign' ? reassignUserId : undefined
      };

      if (!USE_GOBODO_API) {
        approvalProcessStorage.processAction(payload, userId, 'Current User');
      }

      const actionLabels: Record<ApprovalAction, string> = {
        approve: 'approved',
        reject: 'rejected',
        reassign: 'reassigned',
        add_comment: 'commented on',
        recall: 'recalled'
      };

      toast.success('Success', `Request ${actionLabels[actionModal.action]} successfully`);
      closeActionModal();
      loadPendingApprovals();
      setSelectedItems(new Set());
    } catch (error) {
      toast.error('Error', 'Failed to process action');
    } finally {
      setProcessingAction(false);
    }
  };

  // Bulk approve
  const bulkApprove = async () => {
    if (selectedItems.size === 0) return;

    setProcessingAction(true);
    try {
      if (!USE_GOBODO_API) {
        const itemsArray = Array.from(selectedItems);
        for (const requestId of itemsArray) {
          approvalProcessStorage.processAction({
            requestId,
            action: 'approve'
          }, userId, 'Current User');
        }
      }
      toast.success('Success', `${selectedItems.size} request(s) approved`);
      loadPendingApprovals();
      setSelectedItems(new Set());
    } catch (error) {
      toast.error('Error', 'Failed to process bulk approval');
    } finally {
      setProcessingAction(false);
    }
  };

  // Format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Format relative time
  const formatRelativeTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return formatDate(dateString);
  };

  // Get sort icon
  const getSortIcon = (field: SortField) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? (
      <SortAsc className="w-4 h-4" />
    ) : (
      <SortDesc className="w-4 h-4" />
    );
  };

  if (loading) {
    return <StandardLoadingState message="Loading pending approvals..." />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Pending Approvals</h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              {filteredApprovals.length} item{filteredApprovals.length !== 1 ? 's' : ''} awaiting your approval
            </p>
          </div>
          <StandardButton
            variant="ghost"
            size="sm"
            onClick={loadPendingApprovals}
          >
            <RefreshCw className="w-4 h-4 mr-1" />
            Refresh
          </StandardButton>
        </div>

        {/* Stats Summary */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                <Clock className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Total Pending</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{pendingApprovals.length}</p>
              </div>
            </div>
          </div>

          <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-lg">
                <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400" />
              </div>
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Overdue</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {pendingApprovals.filter(a => a.isOverdue).length}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg">
                <Calendar className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
              </div>
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Due Today</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {pendingApprovals.filter(a => {
                    if (!a.dueDate) return false;
                    const due = new Date(a.dueDate);
                    const today = new Date();
                    return due.toDateString() === today.toDateString();
                  }).length}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Avg. Wait</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {pendingApprovals.length > 0
                    ? Math.round(pendingApprovals.reduce((sum, a) => sum + a.daysWaiting, 0) / pendingApprovals.length)
                    : 0}d
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <StandardSearchBar
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder="Search by record name, process, or submitter..."
            />
          </div>
          <div className="flex gap-3">
            <StandardSelect
              value={objectTypeFilter}
              onChange={(e) => setObjectTypeFilter(e.target.value)}
              options={[
                { value: 'all', label: 'All Objects' },
                ...objectTypes.map(type => ({ value: type, label: type }))
              ]}
            />
            <StandardSelect
              value={overdueFilter}
              onChange={(e) => setOverdueFilter(e.target.value)}
              options={[
                { value: 'all', label: 'All Status' },
                { value: 'overdue', label: 'Overdue Only' },
                { value: 'on-track', label: 'On Track' }
              ]}
            />
          </div>
        </div>

        {/* Bulk Actions */}
        {selectedItems.size > 0 && (
          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700 flex items-center gap-4">
            <span className="text-sm text-gray-600 dark:text-gray-400">
              {selectedItems.size} item{selectedItems.size !== 1 ? 's' : ''} selected
            </span>
            <StandardButton
              variant="primary"
              size="sm"
              onClick={bulkApprove}
              disabled={processingAction}
            >
              <CheckCircle className="w-4 h-4 mr-1" />
              Approve Selected
            </StandardButton>
            <StandardButton
              variant="ghost"
              size="sm"
              onClick={() => setSelectedItems(new Set())}
            >
              Clear Selection
            </StandardButton>
          </div>
        )}
      </div>

      {/* Approvals List */}
      {filteredApprovals.length === 0 ? (
        <StandardEmptyState
          icon={<CheckCircle size={48} />}
          title="No Pending Approvals"
          message={searchQuery || objectTypeFilter !== 'all' || overdueFilter !== 'all'
            ? "No approvals match your current filters"
            : "You're all caught up! No items waiting for your approval."
          }
        />
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
          {/* Table Header */}
          <div className="grid grid-cols-[auto_1fr_150px_150px_120px_150px] gap-4 px-6 py-3 bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400">
            <div className="flex items-center">
              <StandardCheckbox
                checked={selectedItems.size === filteredApprovals.length && filteredApprovals.length > 0}
                onChange={selectAll}
              />
            </div>
            <button
              onClick={() => handleSort('recordName')}
              className="flex items-center gap-1 hover:text-gray-700 dark:hover:text-gray-300"
            >
              Record {getSortIcon('recordName')}
            </button>
            <button
              onClick={() => handleSort('processName')}
              className="flex items-center gap-1 hover:text-gray-700 dark:hover:text-gray-300"
            >
              Process {getSortIcon('processName')}
            </button>
            <button
              onClick={() => handleSort('submittedDate')}
              className="flex items-center gap-1 hover:text-gray-700 dark:hover:text-gray-300"
            >
              Submitted {getSortIcon('submittedDate')}
            </button>
            <div>Step</div>
            <div>Actions</div>
          </div>

          {/* Table Body */}
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {filteredApprovals.map(approval => (
              <div key={approval.requestId}>
                <div className="grid grid-cols-[auto_1fr_150px_150px_120px_150px] gap-4 px-6 py-4 items-center hover:bg-gray-50 dark:hover:bg-gray-700/30">
                  <div>
                    <StandardCheckbox
                      checked={selectedItems.has(approval.requestId)}
                      onChange={() => toggleItemSelection(approval.requestId)}
                    />
                  </div>

                  <div className="flex items-center gap-3">
                    <div className={`w-2 h-2 rounded-full ${approval.isOverdue ? 'bg-red-500' : 'bg-green-500'}`} />
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900 dark:text-white">{approval.recordName}</span>
                        <span className="px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded text-xs">
                          {approval.objectType}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                        <User className="w-3 h-3" />
                        <span>{approval.submittedByName}</span>
                        {approval.daysWaiting > 0 && (
                          <>
                            <span>•</span>
                            <span className={approval.isOverdue ? 'text-red-600 dark:text-red-400' : ''}>
                              Waiting {approval.daysWaiting} day{approval.daysWaiting !== 1 ? 's' : ''}
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="text-sm text-gray-600 dark:text-gray-400">{approval.processName}</div>

                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    {formatRelativeTime(approval.submittedDate)}
                    {approval.dueDate && (
                      <div className={`text-xs ${approval.isOverdue ? 'text-red-600 dark:text-red-400' : 'text-gray-500 dark:text-gray-500'}`}>
                        Due: {formatDate(approval.dueDate)}
                      </div>
                    )}
                  </div>

                  <div className="text-sm">
                    <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded text-gray-700 dark:text-gray-300">
                      {approval.currentStepNumber}/{approval.totalSteps}
                    </span>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">{approval.currentStep}</div>
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => openActionModal('approve', approval.requestId, approval.recordName)}
                      className="p-2 text-green-600 hover:bg-green-50 dark:text-green-400 dark:hover:bg-green-900/20 rounded-lg transition-colors"
                      title="Approve"
                    >
                      <CheckCircle className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => openActionModal('reject', approval.requestId, approval.recordName)}
                      className="p-2 text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                      title="Reject"
                    >
                      <XCircle className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => setExpandedItem(expandedItem === approval.requestId ? null : approval.requestId)}
                      className="p-2 text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700 rounded-lg transition-colors"
                      title="More options"
                    >
                      <MoreHorizontal className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                {/* Expanded Actions */}
                {expandedItem === approval.requestId && (
                  <div className="px-6 py-4 bg-gray-50 dark:bg-gray-700/30 border-t border-gray-100 dark:border-gray-600">
                    <div className="flex flex-wrap gap-3">
                      <StandardButton
                        variant="secondary"
                        size="sm"
                        onClick={() => openActionModal('add_comment', approval.requestId, approval.recordName)}
                      >
                        <MessageSquare className="w-4 h-4 mr-1" />
                        Add Comment
                      </StandardButton>
                      <StandardButton
                        variant="secondary"
                        size="sm"
                        onClick={() => openActionModal('reassign', approval.requestId, approval.recordName)}
                      >
                        <UserPlus className="w-4 h-4 mr-1" />
                        Reassign
                      </StandardButton>
                      {onViewRequest && (
                        <StandardButton
                          variant="ghost"
                          size="sm"
                          onClick={() => onViewRequest(approval.requestId)}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          View Request
                        </StandardButton>
                      )}
                      {onViewRecord && (
                        <StandardButton
                          variant="ghost"
                          size="sm"
                          onClick={() => onViewRecord(approval.recordId, approval.objectType)}
                        >
                          <FileText className="w-4 h-4 mr-1" />
                          View Record
                        </StandardButton>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Action Modal */}
      <StandardModal
        isOpen={actionModal.isOpen}
        onClose={closeActionModal}
        title={
          actionModal.action === 'approve' ? 'Approve Request' :
          actionModal.action === 'reject' ? 'Reject Request' :
          actionModal.action === 'reassign' ? 'Reassign Request' :
          actionModal.action === 'add_comment' ? 'Add Comment' :
          'Action'
        }
        size="md"
      >
        <StandardModal.Body>
          <div className="space-y-4">
            <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
              <p className="text-sm text-gray-500 dark:text-gray-400">Record</p>
              <p className="font-medium text-gray-900 dark:text-white">{actionModal.recordName}</p>
            </div>

            {actionModal.action === 'reassign' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Reassign To
                </label>
                <StandardSelect
                  value={reassignUserId}
                  onChange={(e) => setReassignUserId(e.target.value)}
                  options={[
                    { value: '', label: 'Select a user...' },
                    ...mockUsers.map(u => ({ value: u.id, label: u.name }))
                  ]}
                />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Comments {actionModal.action === 'reject' && <span className="text-red-500">*</span>}
              </label>
              <StandardTextarea
                value={actionComments}
                onChange={(e) => setActionComments(e.target.value)}
                placeholder={
                  actionModal.action === 'approve' ? 'Add optional comments...' :
                  actionModal.action === 'reject' ? 'Please provide a reason for rejection...' :
                  'Enter your comments...'
                }
                rows={4}
              />
            </div>

            {actionModal.action === 'approve' && (
              <div className="flex items-center gap-2 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg text-green-700 dark:text-green-400">
                <CheckCircle size={18} />
                <span className="text-sm">This will move the request to the next approval step or mark it as approved.</span>
              </div>
            )}

            {actionModal.action === 'reject' && (
              <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 rounded-lg text-red-700 dark:text-red-400">
                <XCircle size={18} />
                <span className="text-sm">This will reject the request and notify the submitter.</span>
              </div>
            )}
          </div>
        </StandardModal.Body>
        <StandardModal.Footer>
          <StandardButton
            variant="secondary"
            onClick={closeActionModal}
            disabled={processingAction}
          >
            Cancel
          </StandardButton>
          <StandardButton
            variant={
              actionModal.action === 'approve' ? 'primary' :
              actionModal.action === 'reject' ? 'danger' :
              'primary'
            }
            onClick={processAction}
            disabled={
              processingAction ||
              (actionModal.action === 'reject' && !actionComments.trim()) ||
              (actionModal.action === 'reassign' && !reassignUserId)
            }
            leftIcon={
              processingAction ? <RefreshCw size={16} className="animate-spin" /> :
              actionModal.action === 'approve' ? <CheckCircle size={16} /> :
              actionModal.action === 'reject' ? <XCircle size={16} /> :
              actionModal.action === 'reassign' ? <UserPlus size={16} /> :
              actionModal.action === 'add_comment' ? <MessageSquare size={16} /> :
              undefined
            }
          >
            {processingAction ? 'Processing...' :
             actionModal.action === 'approve' ? 'Approve' :
             actionModal.action === 'reject' ? 'Reject' :
             actionModal.action === 'reassign' ? 'Reassign' :
             'Submit'}
          </StandardButton>
        </StandardModal.Footer>
      </StandardModal>
    </div>
  );
};
